import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Link as MuiLink,
    Skeleton,
    Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { themeVars } from '@mds/ui';
import { Link as RouterLink } from 'react-router-dom';
import { PublishedByProps } from '../../ui/PublishedBy';
import { ViewTracker } from '../../ui/ViewTracker';
import { Channel } from '../../../shared/types';
import { formatDisplayDateTime } from '../../../utils/date.utils';

const linkSx = (theme: Theme) => ({
    color: themeVars(theme).palette.link.default,
    '&&:hover': {
        color: themeVars(theme).palette.link.default,
    },
});

const titleLinkSx = {
    color: 'text.primary',
    textDecoration: 'none',
    textDecorationColor: 'currentColor',
    '&&:hover': {
        color: 'text.primary',
        textDecoration: 'none',
    },
    '& .MuiTypography-root': {
        color: 'text.primary',
    },
};

const cardHoverSx = {
    height: '100%',
    bgcolor: 'background.paper',
    transition:
        'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 2,
        borderColor: 'primary.main',
    },
};

export interface ArticlePreviewCardProps {
    title: string;
    publishedData: PublishedByProps;
    articleLink: string;
    summary?: string;
    source: string;
    sourceId?: string;
    sourceDetail?: string;
    channel?: Channel;
    scopeChipLabel?: string;
    reserveScopeChipSpace?: boolean;
}

export interface PinnableArticlePreviewCardProps extends ArticlePreviewCardProps {
    isPinned?: boolean;
    displayMode?: 'card' | 'separated';
    showSeparator?: boolean;
}

const PublishedByLine = ({
    publishedData,
}: {
    publishedData: PublishedByProps;
}) => (
    <Typography
        variant="caption"
        sx={{
            color: 'text.secondary',
        }}
    >
        {formatDisplayDateTime(
            publishedData.date,
            publishedData.momentInTimeFormat
        )}{' '}
        by{' '}
        <MuiLink
            id="published-by-link"
            component={RouterLink}
            to={publishedData.publisher.url}
            underline="none"
            sx={linkSx}
        >
            {publishedData.publisher.name}
        </MuiLink>
    </Typography>
);

const ArticlePreviewCard = (props: ArticlePreviewCardProps) => {
    const {
        title,
        publishedData,
        articleLink,
        source,
        sourceId,
        sourceDetail,
        channel,
        scopeChipLabel,
        reserveScopeChipSpace = false,
    } = props;

    return (
        <Card
            className="article-preview-card"
            variant="outlined"
            sx={cardHoverSx}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    gap: 1,
                }}
            >
                {(scopeChipLabel || channel || reserveScopeChipSpace) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            minHeight: 24,
                        }}
                    >
                        {scopeChipLabel ? (
                            <Chip
                                label={scopeChipLabel}
                                size="small"
                                color="primary"
                                variant="soft"
                            />
                        ) : null}
                        {channel ? (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {channel.name}
                            </Typography>
                        ) : null}
                    </Box>
                )}
                <MuiLink
                    component={RouterLink}
                    to={articleLink}
                    underline="none"
                    color="inherit"
                    sx={titleLinkSx}
                >
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            color: 'text.primary',
                        }}
                    >
                        <ViewTracker
                            source={source}
                            sourceId={sourceId}
                            sourceDetail={sourceDetail}
                        >
                            {title}
                        </ViewTracker>
                    </Typography>
                </MuiLink>
                <PublishedByLine publishedData={publishedData} />
                {props?.summary ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {props.summary}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
};

export const PinnableArticlePreviewCard = (
    props: PinnableArticlePreviewCardProps
) => {
    const {
        title,
        publishedData,
        articleLink,
        source,
        sourceId,
        sourceDetail,
        channel,
        isPinned,
        displayMode = 'card',
        showSeparator = true,
    } = props;

    const content = (
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isPinned || channel ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    {isPinned ? (
                        <Chip label="Pinned" size="small" color="primary" />
                    ) : null}
                    {channel ? (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {channel.name}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}

            <MuiLink
                component={RouterLink}
                to={articleLink}
                underline="none"
                color="inherit"
                sx={titleLinkSx}
            >
                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        color: 'text.primary',
                    }}
                >
                    <ViewTracker
                        source={source}
                        sourceId={sourceId}
                        sourceDetail={sourceDetail}
                    >
                        {title}
                    </ViewTracker>
                </Typography>
            </MuiLink>
            {props?.summary ? (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {props.summary}
                </Typography>
            ) : null}
            <PublishedByLine publishedData={publishedData} />
        </CardContent>
    );

    if (!isPinned && displayMode === 'separated') {
        return (
            <Box
                className="article-preview-card article-preview-card--separated"
                sx={{
                    borderBottom: showSeparator ? 1 : 0,
                    borderColor: 'divider',
                    '& .MuiCardContent-root': {
                        px: 0,
                        py: 2.5,
                    },
                }}
            >
                {content}
            </Box>
        );
    }

    return (
        <Card
            className="article-preview-card"
            variant="outlined"
            sx={cardHoverSx}
        >
            {content}
        </Card>
    );
};

export const ArticlePreviewCardSkeleton = () => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="70%" />
        </CardContent>
    </Card>
);

export default ArticlePreviewCard;
